import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AmplitudeService } from '../analytics/amplitude.service';
import { TrackingCookieService } from '../analytics/tracking-cookie.service';
import { AuthProfileService } from './auth-profile.service';
import { AuthPasswordService } from './auth-password.service';
import {
  AuthResponse,
  SignUpRequest,
  SignInRequest,
  UserType,
  UserProfile,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
} from './auth.models';

/**
 * Main authentication facade.
 *
 * Owns core auth flow (sign in/up/out, tokens, OAuth) and delegates
 * profile management to AuthProfileService and password reset to AuthPasswordService.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly trackingCookieService = inject(TrackingCookieService);
  private readonly profileService = inject(AuthProfileService);
  private readonly passwordService = inject(AuthPasswordService);

  private readonly apiUrl = environment.apiUrl;
  private readonly tokenKey = 'jwt_token';

  private readonly isAuthenticated$$ = new BehaviorSubject<boolean>(this.hasToken());

  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$$.asObservable();
  }

  signUp(request: SignUpRequest, redirectUrl?: string): Observable<AuthResponse> {
    const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/signup${params}`, request).pipe(
      tap((response) => this.handleAuthSuccess(response)),
    );
  }

  signIn(request: SignInRequest, redirectUrl?: string): Observable<AuthResponse> {
    const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/signin${params}`, request).pipe(
      tap((response) => this.handleAuthSuccess(response)),
    );
  }

  signInWithGoogle(redirectUrl?: string, userType?: UserType): void {
    const frontendUrl = window.location.origin;
    const params = new URLSearchParams();
    params.set('frontendUrl', frontendUrl);
    if (redirectUrl) {
      params.set('redirectUrl', redirectUrl);
    }
    if (userType) {
      params.set('userType', userType);
    }
    const trackingData = this.trackingCookieService.getTrackingData();
    if (Object.keys(trackingData).length > 0) {
      params.set('trackingData', JSON.stringify(trackingData));
    }
    window.location.href = `${this.apiUrl}/auth/google?${params.toString()}`;
  }

  signInWithMicrosoft(redirectUrl?: string, userType?: UserType): void {
    const frontendUrl = window.location.origin;
    const params = new URLSearchParams();
    params.set('frontendUrl', frontendUrl);
    if (redirectUrl) {
      params.set('redirectUrl', redirectUrl);
    }
    if (userType) {
      params.set('userType', userType);
    }
    const trackingData = this.trackingCookieService.getTrackingData();
    if (Object.keys(trackingData).length > 0) {
      params.set('trackingData', JSON.stringify(trackingData));
    }
    window.location.href = `${this.apiUrl}/auth/microsoft?${params.toString()}`;
  }

  logout(): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        localStorage.removeItem(this.tokenKey);
        this.isAuthenticated$$.next(false);
        this.profileService.clearProfile();
        this.amplitudeService.reset();
      }),
    );
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.isAuthenticated$$.next(true);
  }

  validateToken(token: string): Observable<boolean> {
    if (!token || token.trim() === '') {
      return of(false);
    }

    // Validate token by calling a protected endpoint
    return this.http
      .get(`${this.apiUrl}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        tap(() => {
          this.setToken(token);
        }),
        map(() => true),
        catchError((error) => {
          console.error('Invalid token provided:', error);
          return of(false);
        }),
      );
  }

  getProfile(): Observable<UserProfile> {
    return this.profileService.getProfile();
  }

  refreshProfile(): Observable<UserProfile> {
    return this.profileService.refreshProfile();
  }

  hasStudentRole(profile: UserProfile): boolean {
    return this.profileService.hasStudentRole(profile);
  }

  hasTeacherRole(profile: UserProfile): boolean {
    return this.profileService.hasTeacherRole(profile);
  }

  isStudent(forceRefresh = false): Observable<boolean> {
    return this.profileService.isStudent(forceRefresh);
  }

  isTeacher(forceRefresh = false): Observable<boolean> {
    return this.profileService.isTeacher(forceRefresh);
  }

  hasPremium(): boolean {
    return this.profileService.hasPremium();
  }

  requestPasswordReset(request: RequestPasswordResetRequest): Observable<PasswordResetResponse> {
    return this.passwordService.requestPasswordReset(request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<PasswordResetResponse> {
    return this.passwordService.resetPassword(request);
  }

  private hasToken(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  private handleAuthSuccess(response: AuthResponse): void {
    this.setToken(response.accessToken);
    this.amplitudeService.setUserId(response.user.id);
  }
}
